import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { track } from "@/lib/api";
import { US_STATES, HEALTH_CONDITIONS } from "@/data/siteContent";
import { useLeadSubmit, ConsentCheckbox, Honeypot, FieldError } from "@/components/forms/shared";

const STEPS = ["Contact Information", "Quote Details", "General Health"];
const CONTACT_METHODS = ["Phone call", "Text message", "Email"];
const BEST_TIMES = ["Morning", "Afternoon", "Evening", "Anytime"];
const COVERAGE_TYPES = ["Final expense", "Term life", "Whole life", "Not sure yet"];
const COVERAGE_AMOUNTS = ["$5,000 – $10,000", "$10,000 – $25,000", "$25,000 – $50,000", "$50,000 – $100,000", "$100,000+", "Not sure yet"];
const BUDGETS = ["Under $50/month", "$50 – $100/month", "$100 – $200/month", "$200+/month", "Not sure yet"];
const PURPOSES = ["Funeral and final expenses", "Income replacement for family", "Mortgage or debt protection", "Leaving money to loved ones", "Other / not sure"];
const HEALTH_RATINGS = ["Excellent", "Good", "Fair", "Poor", "Prefer to discuss"];
const RELATIONSHIPS = ["Spouse", "Child", "Grandchild", "Sibling", "Other family member", "Friend", "Not sure yet"];

function YesNo({ label, name, value, onChange, testid }) {
  return (
    <fieldset>
      <legend className="field-label">{label}</legend>
      <div className="flex gap-3" role="radiogroup">
        {["Yes", "No"].map((opt) => (
          <label key={opt} className={`flex-1 cursor-pointer rounded-md border px-4 py-3 text-center font-semibold min-h-[48px] transition-colors ${
            value === opt ? "border-navy bg-navy text-white" : "border-input bg-white text-ink hover:border-navy"
          }`} data-testid={`${testid}-${opt.toLowerCase()}`}>
            <input type="radio" name={name} value={opt} checked={value === opt} onChange={() => onChange(opt)} className="sr-only" />
            {opt}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function Select({ label, id, value, onChange, options, placeholder = "Select one", error, testid }) {
  return (
    <div>
      <label className="field-label" htmlFor={id}>{label}</label>
      <select id={id} className="field-input" value={value} onChange={(e) => onChange(e.target.value)}
        aria-invalid={!!error} data-testid={testid}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <FieldError message={error} />
    </div>
  );
}

export default function QuoteFormFull() {
  const { submit, submitting } = useLeadSubmit("quote");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "", preferred_contact: "", best_time: "", state: "", zip: "",
    age_or_dob: "", sex: "", tobacco: "", coverage_type: "", coverage_amount: "", monthly_budget: "", purpose: "",
    existing_insurance: "", beneficiary_relationship: "",
    health_rating: "", conditions: [], hospitalized: "", oxygen: "", nursing_home: "", adl: "", diagnoses: "", medications: "",
  });
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState("");
  const [errors, setErrors] = useState({});
  const [started, setStarted] = useState(false);

  const set = (k, v) => {
    if (!started) { setStarted(true); track("quote_form_start", { form: "full" }); }
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.first_name.trim()) e.first_name = "Required.";
      if (!form.last_name.trim()) e.last_name = "Required.";
      if (!form.phone.trim()) e.phone = "Required.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid email.";
      if (!form.state) e.state = "Required.";
      if (!form.zip.trim()) e.zip = "Required.";
    }
    if (step === 1) {
      if (!form.age_or_dob.trim()) e.age_or_dob = "Required.";
      if (!form.tobacco) e.tobacco = "Please select one.";
      if (!form.coverage_type) e.coverage_type = "Please select one.";
    }
    if (step === 2) {
      if (!form.health_rating) e.health_rating = "Please select one.";
      if (!consent) e.consent = "Please confirm your consent so we may contact you.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const toggleCondition = (c) => {
    set("conditions", form.conditions.includes(c)
      ? form.conditions.filter((x) => x !== c)
      : [...form.conditions.filter((x) => x !== "None of these" || c === "None of these"), c]);
  };

  const inputCls = "field-input";
  const L = ({ htmlFor, children }) => <label className="field-label" htmlFor={htmlFor}>{children}</label>;

  return (
    <form
      className="relative rounded-2xl border border-navy-100 bg-white p-6 sm:p-10 shadow-lg"
      onSubmit={(e) => { e.preventDefault(); if (validateStep()) submit(form, { consent, company }); }}
      noValidate
      data-testid="full-quote-form"
    >
      <Honeypot value={company} onChange={setCompany} />

      <ol className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4" aria-label="Form progress">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex items-center gap-2.5 text-base font-semibold ${i === step ? "text-navy" : i < step ? "text-gold-dark" : "text-mutedink/60"}`}
            aria-current={i === step ? "step" : undefined} data-testid={`quote-step-indicator-${i}`}>
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm ${
              i < step ? "border-gold-dark bg-gold-dark text-white" : i === step ? "border-navy bg-navy text-white" : "border-input text-mutedink"
            }`}>
              {i < step ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : i + 1}
            </span>
            {s}
            {i < STEPS.length - 1 && <span className="hidden sm:block h-px w-8 bg-input" aria-hidden="true" />}
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div className="grid gap-5 sm:grid-cols-2" data-testid="quote-step-0">
          <div><L htmlFor="qf-first">First name</L>
            <input id="qf-first" className={inputCls} autoComplete="given-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="quote-first-name" /><FieldError message={errors.first_name} /></div>
          <div><L htmlFor="qf-last">Last name</L>
            <input id="qf-last" className={inputCls} autoComplete="family-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="quote-last-name" /><FieldError message={errors.last_name} /></div>
          <div><L htmlFor="qf-phone">Phone number</L>
            <input id="qf-phone" type="tel" className={inputCls} autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="quote-phone" /><FieldError message={errors.phone} /></div>
          <div><L htmlFor="qf-email">Email address</L>
            <input id="qf-email" type="email" className={inputCls} autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="quote-email" /><FieldError message={errors.email} /></div>
          <Select label="Preferred contact method" id="qf-contact" value={form.preferred_contact} onChange={(v) => set("preferred_contact", v)} options={CONTACT_METHODS} testid="quote-preferred-contact" />
          <Select label="Best time to reach you" id="qf-time" value={form.best_time} onChange={(v) => set("best_time", v)} options={BEST_TIMES} testid="quote-best-time" />
          <Select label="State of residence" id="qf-state" value={form.state} onChange={(v) => set("state", v)} options={US_STATES} error={errors.state} testid="quote-state" />
          <div><L htmlFor="qf-zip">ZIP code</L>
            <input id="qf-zip" className={inputCls} inputMode="numeric" autoComplete="postal-code" value={form.zip} onChange={(e) => set("zip", e.target.value)} data-testid="quote-zip" /><FieldError message={errors.zip} /></div>
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-5 sm:grid-cols-2" data-testid="quote-step-1">
          <div><L htmlFor="qf-age">Date of birth or age</L>
            <input id="qf-age" className={inputCls} placeholder="e.g., 03/12/1958 or 67" value={form.age_or_dob} onChange={(e) => set("age_or_dob", e.target.value)} data-testid="quote-age" /><FieldError message={errors.age_or_dob} /></div>
          <Select label="Sex (only if required for an accurate quote)" id="qf-sex" value={form.sex} onChange={(v) => set("sex", v)} options={["Male", "Female", "Prefer not to say"]} testid="quote-sex" />
          <div className="sm:col-span-2">
            <YesNo label="Have you used tobacco or nicotine in the last 12 months?" name="tobacco" value={form.tobacco}
              onChange={(v) => set("tobacco", v)} testid="quote-tobacco" />
            <FieldError message={errors.tobacco} />
          </div>
          <Select label="Type of coverage you're considering" id="qf-type" value={form.coverage_type} onChange={(v) => set("coverage_type", v)} options={COVERAGE_TYPES} error={errors.coverage_type} testid="quote-coverage-type" />
          <Select label="Desired coverage amount" id="qf-amount" value={form.coverage_amount} onChange={(v) => set("coverage_amount", v)} options={COVERAGE_AMOUNTS} testid="quote-coverage-amount" />
          <Select label="Approximate monthly budget" id="qf-budget" value={form.monthly_budget} onChange={(v) => set("monthly_budget", v)} options={BUDGETS} testid="quote-budget" />
          <Select label="Main purpose for coverage" id="qf-purpose" value={form.purpose} onChange={(v) => set("purpose", v)} options={PURPOSES} testid="quote-purpose" />
          <Select label="Do you currently have life insurance?" id="qf-existing" value={form.existing_insurance} onChange={(v) => set("existing_insurance", v)} options={["Yes", "No", "Not sure"]} testid="quote-existing" />
          <Select label="Who would likely be your beneficiary?" id="qf-beneficiary" value={form.beneficiary_relationship} onChange={(v) => set("beneficiary_relationship", v)} options={RELATIONSHIPS} testid="quote-beneficiary" />
        </div>
      )}

      {step === 2 && (
        <div className="grid gap-6" data-testid="quote-step-2">
          <p className="rounded-lg border border-gold/50 bg-gold/10 p-4 text-base text-navy leading-relaxed" data-testid="health-disclaimer">
            This form is for preliminary quote assistance only and is not an insurance application. Please do not
            include Social Security numbers, banking information, or detailed private medical records.
          </p>
          <Select label="How would you rate your overall health?" id="qf-health" value={form.health_rating} onChange={(v) => set("health_rating", v)} options={HEALTH_RATINGS} error={errors.health_rating} testid="quote-health-rating" />
          <fieldset>
            <legend className="field-label">Do any of the following apply? (Select all that apply)</legend>
            <div className="grid gap-2.5 sm:grid-cols-2">
              {HEALTH_CONDITIONS.map((c) => (
                <label key={c} className={`flex cursor-pointer items-center gap-3 rounded-md border px-4 py-3 min-h-[48px] transition-colors ${
                  form.conditions.includes(c) ? "border-navy bg-navy-50" : "border-input bg-white hover:border-navy"
                }`} data-testid={`quote-condition-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <input type="checkbox" checked={form.conditions.includes(c)} onChange={() => toggleCondition(c)}
                    className="h-5 w-5 shrink-0 accent-[#0B1B3D]" />
                  <span className="text-base">{c}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-5 sm:grid-cols-2">
            <YesNo label="Hospitalized in the past 2 years?" name="hosp" value={form.hospitalized} onChange={(v) => set("hospitalized", v)} testid="quote-hospitalized" />
            <YesNo label="Currently use oxygen?" name="oxygen" value={form.oxygen} onChange={(v) => set("oxygen", v)} testid="quote-oxygen" />
            <YesNo label="Currently in a nursing home or receiving home health care?" name="nh" value={form.nursing_home} onChange={(v) => set("nursing_home", v)} testid="quote-nursing-home" />
            <YesNo label="Need help with daily activities (bathing, dressing, eating)?" name="adl" value={form.adl} onChange={(v) => set("adl", v)} testid="quote-adl" />
          </div>
          <div><L htmlFor="qf-diagnoses">Any major diagnoses we should know about? (Optional — general terms only)</L>
            <input id="qf-diagnoses" className={inputCls} value={form.diagnoses} onChange={(e) => set("diagnoses", e.target.value)} data-testid="quote-diagnoses" /></div>
          <div><L htmlFor="qf-meds">Prescription medications (Optional — a brief summary is fine)</L>
            <input id="qf-meds" className={inputCls} value={form.medications} onChange={(e) => set("medications", e.target.value)} data-testid="quote-medications" /></div>
          <ConsentCheckbox checked={consent} onChange={(v) => { setConsent(v); setErrors((p) => ({ ...p, consent: "" })); }} error={errors.consent} testid="quote" />
        </div>
      )}

      <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        {step > 0 ? (
          <button type="button" className="btn-outline" onClick={() => setStep(step - 1)} data-testid="quote-back-button">Back</button>
        ) : <span />}
        {step < STEPS.length - 1 ? (
          <button type="button" className="btn-primary" onClick={() => { if (validateStep()) setStep(step + 1); }} data-testid="quote-next-button">
            Continue
          </button>
        ) : (
          <button type="submit" className="btn-accent" disabled={submitting} data-testid="quote-submit-button">
            {submitting ? "Sending…" : "Request My Quote"}
          </button>
        )}
      </div>
    </form>
  );
}
